/**
 * Tier-limit upgrade CTA (FR-BILLING-05, AC-BILL-005).
 *
 * Two exports, deliberately in one file because they are two halves of the same
 * contract:
 *
 *   - {@link TierLimitCta} — the presentational card. Anything that already
 *     knows a limit is exhausted (the billing portal's usage panel, an inline
 *     form error) renders it directly.
 *   - {@link TierLimitBoundary} — the app-wide listener. `createApiClient`
 *     raises `APP_EVENTS.tierLimitExceeded` for every 402
 *     `TIER_LIMIT_EXCEEDED`, wherever it came from, and this component turns it
 *     into a modal carrying the service's own limit/usage numbers. Mounted once
 *     in `providers.tsx`, so no screen has to handle 402 itself.
 *
 * The service is the authority on the numbers: `details` carries `limit_key`,
 * `limit`, `current`, `requested`, `current_plan`, `minimum_plan` and
 * `upgrade_path` (see `packages/shared/src/tier-gate.ts`). Nothing here guesses
 * a cap; when a field is missing the CTA degrades to the parts it does have.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSession } from '../app/session-context';
import { ApiError } from '../lib/api-error';
import {
  APP_EVENTS,
  APP_VIEWS,
  ROUTES,
  TIER_LIMIT_ERROR_CODE,
  TIER_LIMIT_STATUS,
  TIER_LIMIT_TOOLTIP_ID,
} from '../lib/constants';

import { OverlayDialog } from './overlay-dialog';
import { Tooltip } from './tooltip';

/** Human labels for the limit keys the tier gate enforces. */
const LIMIT_COPY: Record<string, { noun: string; unit: string }> = {
  seats: { noun: 'Team members', unit: 'seats' },
  teams: { noun: 'Teams', unit: 'teams' },
  games_per_season: { noun: 'Games this season', unit: 'games/season' },
  storage_bytes: { noun: 'Media storage', unit: 'GB' },
  api_rate_per_min: { noun: 'API requests', unit: 'requests/min' },
  ws_connections: { noun: 'Live connections', unit: 'connections' },
};

/** Structured 402 payload, normalised for rendering. */
export interface TierLimitDetail {
  limitKey: string;
  current: number | null;
  limit: number | null;
  requested: number | null;
  currentPlan: string | null;
  minimumPlan: string | null;
  upgradePath: string | null;
  message: string;
}

function readNumber(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readText(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** Label a plan name for display: `varsity` → `Varsity`. */
export function formatPlanName(name: string | null): string | null {
  if (name === null || name === '') return null;
  return name
    .split(/[\s_-]+/u)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * `unknown` → `TierLimitDetail`, or `null` when the error is not a tier-limit
 * rejection. Exported so a screen can render an inline CTA in its own layout
 * instead of (or as well as) the app-wide modal.
 */
export function readTierLimitDetail(error: unknown): TierLimitDetail | null {
  if (!ApiError.isApiError(error)) return null;
  if (error.status !== TIER_LIMIT_STATUS || error.code !== TIER_LIMIT_ERROR_CODE) return null;
  const details = error.details ?? {};
  return {
    limitKey: readText(details, 'limit_key') ?? 'plan',
    current: readNumber(details, 'current'),
    limit: readNumber(details, 'limit'),
    requested: readNumber(details, 'requested'),
    currentPlan: readText(details, 'current_plan'),
    minimumPlan: readText(details, 'minimum_plan'),
    upgradePath: readText(details, 'upgrade_path'),
    message: error.message,
  };
}

export interface TierLimitCtaProps {
  /** Limit key from the 402 payload, e.g. `seats`, `games_per_season`. */
  limit: string;
  current?: number | null;
  /** The cap. `null` renders without a usage ratio. */
  max?: number | null;
  /** Overrides the registry unit label ("seats", "games/season", "GB"). */
  unit?: string;
  /** What the user was trying to do — becomes the headline subject. */
  featureDescription?: string;
  /** Server-supplied human message; shown instead of the generated sentence. */
  message?: string | null;
  currentPlan?: string | null;
  /** Cheapest plan that raises the limit, per the service. */
  minimumPlan?: string | null;
  /** Primary action. Omit to render the CTA without an upgrade button. */
  onUpgrade?: () => void;
  onDismiss?: () => void;
  dismissLabel?: string;
  /** `panel` is the standalone card; `inline` is the compact in-page strip. */
  variant?: 'panel' | 'inline';
  children?: ReactNode;
}

function usageRatio(current: number | null, max: number | null, unit: string): string | null {
  if (current === null || max === null) return null;
  if (unit === 'GB') {
    const toGb = (bytes: number): string => (bytes / 1_000_000_000).toFixed(1);
    return `${toGb(current)} / ${toGb(max)} GB used`;
  }
  return `${current} / ${max} ${unit} used`;
}

export function TierLimitCta({
  limit,
  current = null,
  max = null,
  unit,
  featureDescription,
  message = null,
  currentPlan = null,
  minimumPlan = null,
  onUpgrade,
  onDismiss,
  dismissLabel = 'Not now',
  variant = 'panel',
  children,
}: TierLimitCtaProps): JSX.Element {
  const copy = LIMIT_COPY[limit];
  const unitLabel = unit ?? copy?.unit ?? 'items';
  const subject = featureDescription ?? copy?.noun ?? 'This feature';
  const ratio = usageRatio(current, max, unitLabel);
  const targetPlan = formatPlanName(minimumPlan);
  const planLabel = formatPlanName(currentPlan);

  return (
    <section
      className={`tier-limit tier-limit--${variant}`}
      role="group"
      aria-label={`Plan limit reached: ${subject}`}
    >
      <div className="tier-limit__head">
        <span className="tier-limit__icon" aria-hidden="true">
          !
        </span>
        <h3 className="tier-limit__title">Limit reached: {subject}</h3>
        <Tooltip tooltipId={TIER_LIMIT_TOOLTIP_ID} position="left" />
      </div>
      <p className="tier-limit__body">
        {message ?? `You have reached your plan's ${unitLabel} limit.`}
      </p>
      {ratio === null ? null : (
        <p className="tier-limit__usage">
          <span className="tier-limit__ratio">{ratio}</span>
          {planLabel === null ? null : (
            <span className="tier-limit__plan"> on the {planLabel} plan</span>
          )}
        </p>
      )}
      {children}
      <div className="btn-row tier-limit__actions">
        {onDismiss === undefined ? null : (
          <button type="button" className="btn btn--secondary" onClick={onDismiss}>
            {dismissLabel}
          </button>
        )}
        {onUpgrade === undefined ? null : (
          <button type="button" className="btn btn--primary" onClick={onUpgrade}>
            {targetPlan === null ? 'Compare plans' : `Upgrade to ${targetPlan}`}
          </button>
        )}
      </div>
    </section>
  );
}

/** Where a tier-limit CTA sends the user to fix the problem. */
function upgradeDestination(tenantSlug: string | null, detail: TierLimitDetail): string {
  const query = `?tab=plan&limit=${encodeURIComponent(detail.limitKey)}`;
  if (tenantSlug !== null && tenantSlug !== '') {
    return `/app/${tenantSlug}/${APP_VIEWS.billing}${query}`;
  }
  // No slug in this session (fanatic, or a token that predates slug capture):
  // fall back to the service-supplied path, then to the post-auth router.
  return detail.upgradePath ?? ROUTES.postAuth;
}

/**
 * App-wide 402 surface. Renders nothing until a tier-limit rejection happens
 * anywhere in the app; then shows one modal with the CTA. Repeat rejections
 * replace the payload rather than stacking dialogs.
 */
export function TierLimitBoundary(): JSX.Element | null {
  const [detail, setDetail] = useState<TierLimitDetail | null>(null);
  const { tenantSlug, persona } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    const onTierLimit = (event: Event): void => {
      const parsed = readTierLimitDetail((event as CustomEvent<unknown>).detail);
      if (parsed !== null) setDetail(parsed);
    };
    globalThis.addEventListener?.(APP_EVENTS.tierLimitExceeded, onTierLimit);
    return () => globalThis.removeEventListener?.(APP_EVENTS.tierLimitExceeded, onTierLimit);
  }, []);

  if (detail === null) return null;
  const close = (): void => setDetail(null);
  // Only a Team Admin can change the plan; everyone else gets the same numbers
  // and is told who to ask, rather than a button that would 403.
  const canManageBilling = persona === 'team_admin' || persona === 'platform_admin';

  return (
    <OverlayDialog title="Plan limit reached" onClose={close}>
      <div className="tier-limit-modal">
        <TierLimitCta
          limit={detail.limitKey}
          current={detail.current}
          max={detail.limit}
          message={detail.message}
          currentPlan={detail.currentPlan}
          minimumPlan={detail.minimumPlan}
          variant="inline"
          onDismiss={close}
          dismissLabel="Close"
          onUpgrade={
            canManageBilling
              ? () => {
                  close();
                  navigate(upgradeDestination(tenantSlug, detail));
                }
              : undefined
          }
        >
          {canManageBilling ? null : (
            <p className="muted tier-limit__note">
              Ask a Team Admin to review the team&apos;s plan — plan changes are limited to admins.
            </p>
          )}
        </TierLimitCta>
      </div>
    </OverlayDialog>
  );
}
